import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';

type VerificationState = 'loading' | 'success' | 'error' | 'waiting';

export const VerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { verifyToken, verifyTokenError } = useAuth();
  const { isAuthenticated } = useAuthStore();
  const [state, setState] = useState<VerificationState>('waiting');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Get token from URL
  const token = searchParams.get('token');
  // Get phone from navigation state
  const phone = location.state?.phone;

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace />;
  }

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        // No token - waiting for user to click link in WhatsApp
        setState('waiting');
        return;
      }

      setState('loading');
      try {
        await verifyToken(token);
        setState('success');
        // Redirect to dashboard after success
        setTimeout(() => {
          navigate('/app/dashboard', { replace: true });
        }, 2000);
      } catch (err) {
        setState('error');
        setErrorMessage(
          verifyTokenError?.message || 'Link expirado ou invalido'
        );
      }
    };

    verify();
  }, [token]);

  // Poll for authentication (in case user clicks link in another tab)
  useEffect(() => {
    if (state !== 'waiting') return;

    const checkAuth = () => {
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        // User authenticated in another tab, redirect to dashboard
        navigate('/app/dashboard', { replace: true });
      }
    };

    // Check immediately
    checkAuth();

    // Then poll every 2 seconds
    const interval = setInterval(checkAuth, 2000);

    // Also listen for storage events (when another tab sets the token)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue) {
        navigate('/app/dashboard', { replace: true });
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [state, navigate]);

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div className="text-center py-8">
            <Loader2 className="w-16 h-16 text-primary-600 animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Verificando...
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Aguarde enquanto validamos seu acesso
            </p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Autenticado com sucesso!
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Redirecionando para o dashboard...
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Erro na verificacao
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {errorMessage}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/auth')}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Tentar novamente
            </Button>
          </div>
        );

      case 'waiting':
      default:
        return (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4 relative">
              <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-primary-500"></span>
              </span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Verifique seu WhatsApp
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Enviamos um link magico para{' '}
              <strong className="text-gray-900 dark:text-white">
                {phone ? `+${phone.slice(0, 2)} ${phone.slice(2)}` : 'seu numero'}
              </strong>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
              Clique no link recebido para acessar sua conta
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                Aguardando verificacao...
              </div>

              <button
                onClick={() => navigate('/auth')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Usar outro numero
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-secondary-900 dark:to-secondary-800 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-xl">
            <CardContent>{renderContent()}</CardContent>
          </Card>

          {/* Help text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Nao recebeu o link?{' '}
              <button
                onClick={() => navigate('/auth')}
                className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                Reenviar
              </button>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VerifyPage;
